import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { acceptInvite } from "../services/sharing.service";
import { useAuth } from "../contexts/AuthContext";
import { usePageTitle } from "../hooks/usePageTitle";

type Status = "loading" | "success" | "error";

export default function InviteAccept() {
  usePageTitle('Aceitar Convite');
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [status, setStatus] = useState<Status>("loading");
  const [message, setMessage] = useState<string>("");

  useEffect(() => {
    const token = searchParams.get("token");
    const emailParam = searchParams.get("email");

    if (!token) {
      setStatus("error");
      setMessage(t("inviteAccept.error_invalid_token"));
      return;
    }

    (async () => {
      try {
        const email = emailParam || user?.email || undefined;
        const response = await acceptInvite(token, email);
        const isProject = response?.share?.resource_type === "project";
        setMessage(t(isProject ? "inviteAccept.success_message_project" : "inviteAccept.success_message_meeting"));
        setStatus("success");
      } catch (error: any) {
        console.error(error);
        const errorMessage =
          error?.response?.data?.message || error?.message || t("inviteAccept.error_generic");
        setMessage(errorMessage);
        setStatus("error");
      }
    })();
  }, [searchParams, user, t]);

  const handleNavigate = () => {
    if (user) {
      navigate("/dashboard", { replace: true });
    } else {
      navigate("/login", { replace: true });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center px-4">
      <div className="bg-white shadow-xl rounded-2xl max-w-lg w-full p-10 text-center">
        <img src="/logo-stant.svg" alt="Stant" className="w-36 h-auto mx-auto mb-6" />
        {status === "loading" && (
          <div>
            <div className="animate-spin h-10 w-10 border-4 border-[#013EFB] border-t-transparent rounded-full mx-auto mb-6"></div>
            <h1 className="text-xl font-semibold text-gray-800">{t("inviteAccept.validating_title")}</h1>
            <p className="text-sm text-gray-500 mt-2">
              {t("inviteAccept.validating_description")}
            </p>
          </div>
        )}

        {status === "success" && (
          <div className="space-y-4">
            <h1 className="text-2xl font-semibold text-gray-800">{t("inviteAccept.success_title")}</h1>
            <p className="text-sm text-gray-600">{message}</p>
            <button
              onClick={handleNavigate}
              className="mt-6 inline-flex items-center justify-center px-6 py-3 bg-[#013EFB] text-white font-medium rounded-md hover:bg-[#012BC8] transition-colors"
            >
              {t("inviteAccept.go_to_dashboard")}
            </button>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-4">
            <h1 className="text-2xl font-semibold text-red-600">{t("inviteAccept.error_title")}</h1>
            <p className="text-sm text-gray-600">{message}</p>
            <button
              onClick={handleNavigate}
              className="mt-6 inline-flex items-center justify-center px-6 py-3 border border-[#013EFB] text-[#013EFB] font-medium rounded-md hover:bg-blue-50 transition-colors"
            >
              {t("back")}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

